<template>
  <div class="product-content">
    <div class="toolbar">
      <el-input
        v-model="searchQuery"
        placeholder="搜索产品名称..."
        style="width: 300px"
        @input="onSearchInput"
        clearable
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
      <el-button type="primary" @click="showAddModal">新增产品</el-button>
    </div>

    <el-table :data="list" style="width: 100%" border>
      <el-table-column prop="id" label="ID" width="80"></el-table-column>
      <el-table-column prop="title" label="产品名称"></el-table-column>
      <el-table-column prop="category_id" label="分类" width="120">
        <template #default="scope">
          {{ getCategoryName(scope.row) }}
        </template>
      </el-table-column>
      <el-table-column prop="cover_photo_path" label="封面图片" width="100">
        <template #default="scope">
          <el-image
            v-if="scope.row.cover_photo_path"
            :src="storageSrc(scope.row.cover_photo_path)"
            style="width: 50px; height: 50px"
            fit="cover"
          ></el-image>
          <span v-else>暂无图片</span>
        </template>
      </el-table-column>
      <el-table-column prop="summary" label="摘要" show-overflow-tooltip></el-table-column>
      <el-table-column prop="author" label="作者" width="100"></el-table-column>
      <el-table-column prop="date" label="日期" width="120"></el-table-column>
      <el-table-column prop="body" label="富文本内容" min-width="160" show-overflow-tooltip>
        <template #default="scope">
          {{ stripHtmlPreview(scope.row.body) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="scope">
          <el-button type="primary" size="small" @click="editItem(scope.row)">编辑</el-button>
          <el-button type="danger" size="small" @click="deleteItem(scope.row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager-wrap">
      <el-pagination
        :current-page="page"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @current-change="onPageChange"
        @size-change="onPageSizeChange"
      />
    </div>

    <el-dialog
      v-model="showModal"
      :title="isEditing ? '编辑产品' : '新增产品'"
      width="700px"
      destroy-on-close
      @opened="onDialogOpened"
      @closed="onDialogClosed"
    >
      <el-form :model="formData" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="产品名称" prop="title">
          <el-input v-model="formData.title" placeholder="请输入产品名称"></el-input>
        </el-form-item>
        <el-form-item label="分类" prop="category_id">
          <el-select v-model="formData.category_id" placeholder="请选择分类" style="width: 100%">
            <el-option
              v-for="category in categoryList"
              :key="category.id"
              :label="category.name"
              :value="category.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="封面图片" prop="cover_photo_path">
          <el-upload
            class="upload-demo"
            action=""
            accept="image/*"
            :show-file-list="false"
            :http-request="handleCoverHttpRequest"
            :before-upload="beforeCoverUpload"
            :limit="1"
            list-type="picture-card"
            :disabled="uploading"
          >
            <el-icon v-if="!formData.cover_photo_path && !uploading" class="upload-icon"><Plus /></el-icon>
            <el-icon v-if="uploading" class="upload-icon is-loading"><Loading /></el-icon>
            <el-image v-else-if="formData.cover_photo_path" :src="storageSrc(formData.cover_photo_path)" class="upload-image" fit="cover"></el-image>
          </el-upload>
        </el-form-item>
        <el-form-item label="摘要" prop="summary">
          <el-input v-model="formData.summary" type="textarea" :rows="3" placeholder="请输入摘要"></el-input>
        </el-form-item>
        <el-form-item label="作者" prop="author">
          <el-input v-model="formData.author" placeholder="请输入作者"></el-input>
        </el-form-item>
        <el-form-item label="日期" prop="date">
          <el-date-picker
            v-model="formData.date"
            type="date"
            placeholder="选择日期"
            style="width: 100%"
            value-format="YYYY-MM-DD"
          ></el-date-picker>
        </el-form-item>
        <!-- 弹窗未完全展开时初始化会导致宽度为 0：等 @opened 后再挂载编辑器 -->
        <el-form-item label="富文本内容" prop="body" class="form-item-rich">
          <div v-if="editorMountReady" class="rich-editor-wrap">
            <!-- Toolbar 必须在 editor 实例存在后再挂载，:editor="null" 会触发 Vue runtime flags 报错 -->
            <Toolbar
              v-if="editorRef"
              class="rich-toolbar"
              :editor="editorRef"
              :defaultConfig="toolbarConfig"
              mode="default"
            />
            <Editor
              class="rich-editor"
              v-model="formData.body"
              :defaultConfig="editorConfig"
              mode="default"
              @onCreated="handleCreated"
            />
          </div>
          <div v-else class="rich-editor-skeleton">编辑器加载中…</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="closeModal">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { Search, Plus, Loading } from '@element-plus/icons-vue'
import { getProductList, getProductDetail, addProduct, updateProduct, deleteProduct, getProductCategoryList, uploadFile } from '@/api/index'
import {
  resolveUploadedImageUrl as buildUploadImageUrl,
  getUploadRelativePath,
  getStorageFileUrl,
  absolutizeStorageUrlsInHtml,
  compressImageIfNeeded
} from '@/utils/upload'
import { parseListAndPagination } from '@/utils/pagination'
import '@wangeditor/editor/dist/css/style.css'
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import { markRaw } from 'vue'

/** 与表单、接口提交字段一致（id / title / category_id / cover_photo_path / summary / author / date / body） */
function createEmptyProductForm() {
  return {
    id: null,
    title: '',
    category_id: null,
    cover_photo_path: '',
    summary: '',
    author: '',
    date: '',
    body: ''
  }
}

export default {
  name: 'ProductPage',
  components: {
    Search,
    Plus,
    Loading,
    Editor,
    Toolbar
  },
  data() {
    return {
      searchQuery: '',
      showModal: false,
      /** 弹窗 opened 后再 true，避免 WangEditor 在宽度为 0 时初始化 */
      editorMountReady: false,
      isEditing: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      searchDebounceTimer: null,
      categoryList: [],
      uploading: false,
      submitting: false,
      // 不要用 shallowRef 放在 data() 里：Vue 会解包 ref，导致 this.editorRef 不是 Ref，.value 报错
      editorRef: null,
      toolbarConfig: {},
      formData: createEmptyProductForm(),
      rules: {
        title: [
          { required: true, message: '请输入产品名称', trigger: 'blur' }
        ],
        category_id: [
          { required: true, message: '请选择分类', trigger: 'change' }
        ],
        
        
      }
    }
  },
  beforeUnmount() {
    this.destroyEditorIfAny()
  },
  created() {
    this.initProductPage()
  },
  computed: {
    /** WangEditor 必须配置上传：用项目已有接口，避免「没有配置上传地址」 */
    editorConfig() {
      return {
        placeholder: '请输入富文本内容...',
        MENU_CONF: {
          uploadImage: {
            maxFileSize: 5 * 1024 * 1024,
            maxNumberOfFiles: 10,
            allowedFileTypes: ['image/*'],
            customUpload: (file, insertFn) => this.editorImageCustomUpload(file, insertFn)
          }
        }
      }
    }
  },
  methods: {
    /** 先拉分类再拉产品，避免表格渲染时 categoryList 仍为空 */
    async initProductPage() {
      await this.loadCategoryList()
      await this.loadData()
    },
    /** 统一解析列表接口的 data（数组 / list / data / items） */
    parseApiListPayload(res) {
      const d = res?.data
      if (!d) return []
      if (Array.isArray(d)) return d
      if (Array.isArray(d.list)) return d.list
      if (Array.isArray(d.data)) return d.data
      if (Array.isArray(d.items)) return d.items
      return []
    },
    parseApiDetailPayload(res) {
      const root = res && typeof res === 'object' ? res : null
      const d = root?.data
      if (root && root.id != null) return root
      if (Array.isArray(root)) return root[0] || null
      if (!d) return null
      if (Array.isArray(d)) return d[0] || null
      if (d.data && typeof d.data === 'object' && !Array.isArray(d.data)) return d.data
      if (d.item && typeof d.item === 'object') return d.item
      if (typeof d === 'object') return d
      return null
    },
    /**
     * 将接口单行数据规范为表单结构，兼容常见别名字段，保证表格列与编辑回显一致
     */
    normalizeProductRow(row) {
      const empty = createEmptyProductForm()
      if (!row || typeof row !== 'object') return { ...empty }
      const pick = (...keys) => {
        for (const k of keys) {
          const v = row[k]
          if (v !== undefined && v !== null && v !== '') return v
        }
        return undefined
      }
      const str = (v, fallback = '') => (v != null && v !== '' ? String(v) : fallback)
      const toYmd = (v) => {
        if (v == null || v === '') return ''
        const s = String(v)
        const m = s.match(/(\d{4}-\d{2}-\d{2})/)
        return m ? m[1] : s.slice(0, 10)
      }

      const titleV = pick('title', 'name', 'product_name', 'product_title')
      const catV = pick('category_id', 'categoryId', 'product_category_id')
      const catObjId = row.product_category?.id ?? row.category?.id ?? null
      const coverV = pick('cover_photo_path', 'cover_image', 'cover', 'image', 'thumb')
      const summaryV = pick('summary', 'description', 'desc', 'abstract')
      const authorV = pick('author', 'writer', 'creator_name')
      const dateV = pick('date', 'published_at', 'publish_date', 'created_at')
      const bodyV = pick('body', 'content', 'detail', 'html', 'rich_text')
      const bodyStr = str(bodyV, str(row.body, empty.body))

      return {
        id: row.id != null ? row.id : empty.id,
        title: str(titleV, str(row.title, empty.title)),
        category_id:
          catV != null
            ? catV
            : catObjId != null
              ? catObjId
            : row.category_id != null
              ? row.category_id
              : row.product_category_id != null
                ? row.product_category_id
                : empty.category_id,
        cover_photo_path: str(coverV, str(row.cover_photo_path, empty.cover_photo_path)),
        summary: str(summaryV, str(row.summary, empty.summary)),
        author: str(authorV, str(row.author, empty.author)),
        date: dateV != null ? toYmd(dateV) : toYmd(row.date) || empty.date,
        // 兼容后端已存的富文本图片地址（可能含 /storage 前缀），在编辑器里先统一到当前 STORAGE_BASE
        body: absolutizeStorageUrlsInHtml(bodyStr || '')
      }
    },
    stripHtmlPreview(html) {
      if (html == null || html === '') return '—'
      return String(html)
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 120) || '—'
    },
    storageSrc(path) {
      return getStorageFileUrl(path)
    },
    async editorImageCustomUpload(file, insertFn) {
      try {
        if (file.size > 2 * 1024 * 1024) {
          this.$message.info('图片较大，正在压缩…')
        }
        const fileToUpload = await compressImageIfNeeded(file)
        const formData = new FormData()
        formData.append('file', fileToUpload)
        const res = await uploadFile(formData)
        const imageUrl = buildUploadImageUrl(res)
        if (!imageUrl) {
          throw new Error('服务器未返回图片地址')
        }
        insertFn(imageUrl, '图片', imageUrl)
      } catch (e) {
        console.error('富文本图片上传失败:', e)
        this.$message.error(e.message || '图片上传失败')
      }
    },
    onDialogOpened() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.editorMountReady = true
        })
      })
    },
    onDialogClosed() {
      this.editorMountReady = false
      // 弹窗 destroy-on-close 后子组件会卸载，只清空引用，避免重复 destroy
      this.editorRef = null
    },
    destroyEditorIfAny() {
      const editor = this.editorRef
      if (editor == null) return
      try {
        editor.destroy()
      } catch (e) {
        /* 可能已被子组件销毁 */
      }
      this.editorRef = null
    },
    handleCreated(editor) {
      // 勿把 editor 做成响应式 Proxy，否则弹窗内点「图片」等会报 Cannot find textarea instance by editor
      this.editorRef = markRaw(editor)
      this.$nextTick(() => {
        try {
          editor.focus?.()
        } catch (e) {
          /* ignore */
        }
      })
    },
    async loadData() {
      try {
        const params = { page: this.page, per_page: this.pageSize }
        const q = (this.searchQuery || '').trim()
        if (q) params.title = q
        const res = await getProductList(params)
        const { rows, total } = parseListAndPagination(res)
        let mapped = rows.map((row) => this.normalizeProductRow(row))
        if (mapped.length === 0 && total > 0 && this.page > 1) {
          this.page -= 1
          return await this.loadData()
        }
        this.list = mapped
        this.total = total
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('加载数据失败')
      }
    },
    async loadCategoryList() {
      try {
        const res = await getProductCategoryList()
        this.categoryList = this.parseApiListPayload(res)
      } catch (error) {
        console.error('加载分类列表失败:', error)
        this.categoryList = []
      }
    },
    getCategoryName(row) {
      const id = row?.category_id ?? row?.product_category_id
      if (id === null || id === undefined || id === '') return '—'
      const c = this.categoryList.find((item) => String(item.id) === String(id))
      return c?.name ?? `分类#${id}`
    },
    onSearchInput() {
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.page = 1
        this.loadData()
      }, 400)
    },
    onPageChange(p) {
      if (p == null) return
      this.page = p
      this.loadData()
    },
    onPageSizeChange(size) {
      this.pageSize = size
      this.page = 1
      this.loadData()
    },
    showAddModal() {
      this.isEditing = false
      this.formData = createEmptyProductForm()
      this.showModal = true
    },
    async editItem(row) {
      this.isEditing = true
      try {
        const res = await getProductDetail(row.id)
        const detail = this.parseApiDetailPayload(res)
        this.formData = this.normalizeProductRow(detail || row)
      } catch (error) {
        console.error('加载详情失败:', error)
        this.$message.warning('详情加载失败，已使用列表数据回填')
        this.formData = this.normalizeProductRow(row)
      }
      this.showModal = true
    },
    async deleteItem(id) {
      this.$confirm('确定要删除这条记录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await deleteProduct(id)
          await this.loadData()
          this.$message({
            type: 'success',
            message: '删除成功!'
          })
        } catch (error) {
          console.error('删除失败:', error)
          this.$message.error('删除失败!')
        }
      }).catch(() => {
        this.$message({
          type: 'info',
          message: '已取消删除'
        })
      })
    },
    beforeCoverUpload(file) {
      const raw = file.raw != null ? file.raw : file
      if (!raw.type || !String(raw.type).includes('image')) {
        this.$message.error('只能上传图片文件!')
        return false
      }
      return true
    },
    async handleCoverHttpRequest(options) {
      const rawFile = options.file.raw != null ? options.file.raw : options.file
      this.uploading = true
      try {
        if (rawFile.size > 2 * 1024 * 1024) {
          this.$message.info('图片较大，正在压缩…')
        }
        const fileToUpload = await compressImageIfNeeded(rawFile)
        const formData = new FormData()
        formData.append('file', fileToUpload)
        const res = await uploadFile(formData)
        const relPath = getUploadRelativePath(res)
        if (!relPath) {
          throw new Error('服务器未返回图片路径')
        }
        this.formData.cover_photo_path = relPath
        if (typeof options.onSuccess === 'function') {
          options.onSuccess(res)
        }
        this.$message.success('图片上传成功!')
      } catch (error) {
        console.error('上传失败:', error)
        if (typeof options.onError === 'function') {
          options.onError(error)
        }
        this.$message.error('图片上传失败!')
      } finally {
        this.uploading = false
      }
    },
    async handleSubmit() {
      this.$refs.formRef.validate(async (valid) => {
        if (valid) {
          this.submitting = true
          try {
            const payload = {
              ...this.formData,
              body: absolutizeStorageUrlsInHtml(this.formData.body || '')
            }
            if (this.isEditing) {
              await updateProduct(payload)
              this.$message.success('编辑成功!')
            } else {
              await addProduct(payload)
              this.$message.success('新增成功!')
            }
            await this.loadData()
            this.closeModal()
          } catch (error) {
            console.error('操作失败:', error)
            this.$message.error(this.isEditing ? '编辑失败!' : '新增失败!')
          } finally {
            this.submitting = false
          }
        }
      })
    },
    closeModal() {
      this.showModal = false
      this.formData = createEmptyProductForm()
    }
  }
}
</script>

<style scoped>
.product-content {
  height: 100%;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 10px;
}

.pager-wrap {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.upload-demo {
  text-align: center;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: border-color 0.3s;
}

.upload-demo:hover {
  border-color: #409eff;
}

.upload-icon {
  font-size: 28px;
  color: #8c939d;
  width: 148px;
  height: 148px;
  line-height: 148px;
  text-align: center;
}

.upload-icon.is-loading {
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.upload-image {
  width: 148px;
  height: 148px;
  display: block;
}

/* 富文本：避免 el-form-item flex 子项把编辑区压成一条竖线 */
.form-item-rich :deep(.el-form-item__content) {
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

.rich-editor-wrap {
  width: 100%;
  max-width: 100%;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  overflow: hidden;
}

.rich-toolbar {
  border-bottom: 1px solid #ccc;
}

.rich-toolbar :deep(.w-e-toolbar) {
  width: 100% !important;
}

.rich-editor {
  height: 400px;
  overflow-y: hidden;
}

.rich-editor :deep(.w-e-text-container),
.rich-editor :deep(.w-e-scroll) {
  width: 100% !important;
}

.rich-editor :deep(.w-e-text-container) {
  min-height: 360px;
}

.rich-editor-skeleton {
  width: 100%;
  height: 400px;
  line-height: 400px;
  text-align: center;
  color: #909399;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  background: #fafafa;
  box-sizing: border-box;
}
</style>
